/**
 * Owner: Masa Bayu
 * File: author-analytics.ts
 * Date:Time: 2026-09-16 15:10
 * Description: T6-E6 AuthorAnalyticsSlice — aggregates from SaleRecords, not Reader telemetry.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { authorizeAuthorSurfaceAccess } from './author-account';
import { assertT6E4SecurityIntact, assertT6E5SecurityIntact, assertT6E6SecurityIntact, } from './author-security';
import { createQxreadId } from './id';
import { assertTimestamp, assertValidId } from './guards';
function assertNonNegativeInt(value, label) {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
        throw new Error(`${label} must be a non-negative integer`);
    }
    return value;
}
function assertAudienceInput(input) {
    if (input === undefined) {
        return null;
    }
    assertTimestamp(input.asOf, 'QXRead audience asOf');
    return Object.freeze({
        followerCount: assertNonNegativeInt(input.followerCount, 'QXRead followerCount'),
        profileViews: assertNonNegativeInt(input.profileViews, 'QXRead profileViews'),
        asOf: input.asOf,
    });
}
/** Aggregate author-owned sales into analytics slices — never raw Reader telemetry. */
export function projectAuthorAnalyticsSlice(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead analytics slice');
    assertT6E4SecurityIntact();
    assertT6E6SecurityIntact();
    assertValidId(input.authorAccount.authorAccountId, 'QXRead authorAccountId');
    const projectedAt = input.projectedAt ?? new Date().toISOString();
    assertTimestamp(projectedAt, 'QXRead analytics projectedAt');
    const periodLabel = typeof input.periodLabel === 'string' && input.periodLabel.length > 0
        ? input.periodLabel
        : 'rolling';
    const audience = assertAudienceInput(input.audience);
    const byPublication = new Map();
    let currency = 'USD';
    for (const sale of input.saleRecords) {
        if (sale.authorAccountId !== input.authorAccount.authorAccountId || sale.state === 'void') {
            continue;
        }
        currency = sale.currency;
        const existing = byPublication.get(sale.publicationId);
        const quantity = assertNonNegativeInt(sale.quantity, 'QXRead analytics quantity');
        const subtotal = assertNonNegativeInt(sale.sellerSubtotalMinorUnits, 'QXRead analytics subtotal');
        if (existing === undefined) {
            byPublication.set(sale.publicationId, Object.freeze({
                publicationId: sale.publicationId,
                title: sale.title,
                unitsSold: quantity,
                sellerSubtotalMinorUnits: subtotal,
                currency: sale.currency,
            }));
        }
        else {
            byPublication.set(sale.publicationId, Object.freeze({
                ...existing,
                unitsSold: existing.unitsSold + quantity,
                sellerSubtotalMinorUnits: existing.sellerSubtotalMinorUnits + subtotal,
            }));
        }
    }
    const publicationRollups = Object.freeze([...byPublication.values()]);
    let totalUnitsSold = 0;
    let totalSellerSubtotalMinorUnits = 0;
    for (const rollup of publicationRollups) {
        totalUnitsSold += rollup.unitsSold;
        totalSellerSubtotalMinorUnits += rollup.sellerSubtotalMinorUnits;
    }
    return Object.freeze({
        sliceId: createQxreadId('qxrdanly'),
        authorAccountId: input.authorAccount.authorAccountId,
        accountId: input.authorAccount.accountId,
        periodLabel,
        publicationRollups,
        totalUnitsSold,
        totalSellerSubtotalMinorUnits,
        currency,
        followerCount: audience?.followerCount ?? null,
        profileViews: audience?.profileViews ?? null,
        projectedAt,
        derivedFromSaleRecordsOnly: true,
        includesRawReaderTelemetry: false,
        inventedByAuthorUi: false,
    });
}
export function projectAuthorAnalyticsSummary(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead analytics summary');
    assertT6E5SecurityIntact();
    assertT6E6SecurityIntact();
    const access = authorizeAuthorSurfaceAccess({
        session: input.session,
        account: input.account,
        authorAccount: input.authorAccount,
    });
    const projectedAt = input.projectedAt ?? new Date().toISOString();
    assertTimestamp(projectedAt, 'QXRead analytics summary projectedAt');
    if (input.slice.authorAccountId !== input.authorAccount.authorAccountId) {
        throw new Error('QXRead analytics slice must belong to the authorized AuthorAccount');
    }
    const chartSeries = Object.freeze(input.slice.publicationRollups.map((rollup) => Object.freeze({
        label: rollup.title,
        unitsSold: rollup.unitsSold,
        sellerSubtotalMinorUnits: rollup.sellerSubtotalMinorUnits,
    })));
    return Object.freeze({
        authorAccountId: input.authorAccount.authorAccountId,
        accountId: input.authorAccount.accountId,
        slice: input.slice,
        chartSeries,
        projectedAt,
        accessAllowed: access.allowed,
        accessReason: access.reason,
    });
}
//# sourceMappingURL=author-analytics.js.map